import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { FocalArea } from '../models/focal-area.model';

interface AISuggestion extends FocalArea {
	// AI suggestions have the same structure as FocalArea
}

interface AISuggestionsResponse {
	suggestions: AISuggestion[];
	message?: string;
}

@Component({
	selector: 'app-ai-suggestions',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatButtonModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		MatMenuModule
	],
	templateUrl: './ai-suggestions.component.html',
	styleUrls: ['./ai-suggestions.component.scss']
})
export class AiSuggestionsComponent {
	@Input() imageUrl: string = '';
	@Output() addSuggestion = new EventEmitter<FocalArea>();
	@Output() previewSuggestion = new EventEmitter<FocalArea>();

	suggestions: AISuggestion[] = [];
	isLoading: boolean = false;
	error: string | null = null;
	hasAnalyzed: boolean = false;
	showCustomInstructions: boolean = false;
	customInstructions: string = '';

	private readonly STORAGE_KEY = 'ai-focal-suggestions-custom-instructions';

	constructor(private http: HttpClient) {}

	startAnalysis(): void {
		if (!this.imageUrl) {
			this.error = 'No image URL provided';
			return;
		}

		this.isLoading = true;
		this.error = null;
		this.hasAnalyzed = false;

		const headers = new HttpHeaders({
			'origin': window.location.origin
		});

		const body: any = { imageUrl: this.imageUrl };

		// Only send custom instructions if the textarea is shown AND has content
		if (this.showCustomInstructions && this.customInstructions && this.customInstructions.trim()) {
			body.customInstructions = this.customInstructions.trim();
			// Save to localStorage when submitting
			this.saveCustomInstructions(this.customInstructions.trim());
		}

		this.http.post<AISuggestionsResponse>(
			`${environment.apiUrl}/integrations/ai-suggest-focal-areas`,
			body,
			{ headers }
		).subscribe({
			next: (response) => {
				this.suggestions = response.suggestions || [];
				this.isLoading = false;
				this.hasAnalyzed = true;
				this.error = null;
			},
			error: (err) => {
				console.error('Error loading AI suggestions:', err);
				this.error = err.error?.message || 'Failed to analyze image. Please try again.';
				this.isLoading = false;
				this.hasAnalyzed = true;
			}
		});
	}

	toggleCustomInstructions(): void {
		this.showCustomInstructions = !this.showCustomInstructions;

		// When showing the textarea, load saved instructions from localStorage
		if (this.showCustomInstructions) {
			this.loadCustomInstructions();
		}
	}

	private saveCustomInstructions(instructions: string): void {
		try {
			localStorage.setItem(this.STORAGE_KEY, instructions);
		} catch (error) {
			console.error('Failed to save custom instructions to localStorage:', error);
		}
	}

	private loadCustomInstructions(): void {
		try {
			const saved = localStorage.getItem(this.STORAGE_KEY);
			if (saved) {
				this.customInstructions = saved;
			}
		} catch (error) {
			console.error('Failed to load custom instructions from localStorage:', error);
		}
	}

	onAddSuggestion(suggestion: AISuggestion, index: number): void {
		// Emit the suggestion to parent
		const focalArea: FocalArea = {
			...suggestion,
			isDefault: false,
			tags: Array.isArray(suggestion.tags) ? [...suggestion.tags] : []
		};
		this.addSuggestion.emit(focalArea);

		// Remove from suggestions list
		this.suggestions = this.suggestions.filter((_, i) => i !== index);
	}

	onRetry(): void {
		this.startAnalysis();
	}

	onPreviewSuggestion(suggestion: AISuggestion, index: number): void {
		// Emit the suggestion to parent for preview
		const focalArea: FocalArea = {
			...suggestion,
			isDefault: false,
			tags: Array.isArray(suggestion.tags) ? [...suggestion.tags] : []
		};
		this.previewSuggestion.emit(focalArea);
	}

	clearSuggestions(): void {
		this.suggestions = [];
		this.hasAnalyzed = false;
		this.error = null;
	}
}
